import {Component, EventEmitter, Inject, Input, LOCALE_ID, Output, signal} from '@angular/core';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {CommonModule} from '@angular/common';
import {MatTooltip} from '@angular/material/tooltip';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {TranslateModule} from '@ngx-translate/core';
import {PrimusInstanceDetails} from '../../core/primus-backend-instance.service';
import {MatRipple} from '@angular/material/core';
import {RecentRegistrationsListComponent} from './recent-registrations-list/recent-registrations-list.component';
import {RecentRegistration} from "../../core/definitions/recent.registration";
import {WelcomeListItem} from "../../core/definitions/welcome-list.item";
import {SearchSuggestion} from "./search.suggestion";
import {Params, RouterLink} from "@angular/router";

const DEFAULT_NUMBER_OF_LIST_ITEMS = 7;

@Component({
    selector: 'app-welcome-screen-ui',
    imports: [MatButton, MatFormField, MatIcon, MatInput, MatLabel, MatMenu, MatMenuItem, MatMenuTrigger, MatSuffix, CommonModule, MatTooltip, MatProgressSpinner, MatIconButton, TranslateModule, MatRipple, RecentRegistrationsListComponent, RouterLink],
    templateUrl: './welcome-screen-ui.component.html',
    styleUrl: './welcome-screen-ui.component.scss'
})
export class WelcomeScreenUiComponent {
  @Input() storedSearches: WelcomeListItem[] = [];
  @Input() objectList: WelcomeListItem[] = [];
  @Input() categories: WelcomeListItem[] = [];
  @Input() loadingStoredSearches = false;
  @Input() loadingObjectList = false;
  @Input() loadingCategories = false;
  @Input() canUseAdvancedSearch = false;
  @Input() museumInstance: PrimusInstanceDetails | null = null;
  @Input() loadingRecentRegistrations = false;
  @Input() recentRegistrations: RecentRegistration[] = [];
  @Input() onlyMyOwnRegistrations = false;

  @Output() advancedSearchClick = new EventEmitter<any>();
  @Output() listItemClick = new EventEmitter<WelcomeListItem>();
  @Output() searchSubmit = new EventEmitter<{ query: string; }>();
  @Output() editStoredSearchesClick = new EventEmitter<void>();
  @Output() searchSuggestionClick = new EventEmitter<SearchSuggestion>();
  @Output() recentRegistrationClick = new EventEmitter<RecentRegistration>();
  @Output() onlyMyOwnRegistrationsChange = new EventEmitter<boolean>();

  numberOfStoredSearches = signal(DEFAULT_NUMBER_OF_LIST_ITEMS);
  numberOfArtifacts = signal(DEFAULT_NUMBER_OF_LIST_ITEMS);
  protected readonly DEFAULT_NUMBER_OF_LIST_ITEMS = DEFAULT_NUMBER_OF_LIST_ITEMS;

  constructor(
    @Inject(LOCALE_ID) public locale: string
  ) {
  }

  getListItemRouterLink(): any[] {
    return ['/search'];
  }

  getListItemQueryParams(item: WelcomeListItem): Params {
    const params: Params = {
      clear: true
    };

    if ('path' in item && typeof item.path === 'string') {
      params.path = item.path;
    }
    else if ('stored_search' in item && 'storedSearchId' in item) {
      params.cur_stored_search_id = item.storedSearchId;
    }
    else {
      console.warn('Could not navigate: missing path in listItem.', item);
      return;
    }

    params.search_result_view = item.view;

    return params;
  }

  onAdvancedSearchClick() {
    this.advancedSearchClick.emit();
  }

  onListItemClick(item: WelcomeListItem) {
    this.listItemClick.emit(item);
  }

  onShowMoreStoredSearches() {
    this.numberOfStoredSearches.set(this.storedSearches.length);
  }

  onShowMoreArtifacts() {
    this.numberOfArtifacts.set(this.objectList.length);
  }

  onShowLessStoredSearches() {
    this.numberOfStoredSearches.set(DEFAULT_NUMBER_OF_LIST_ITEMS);
  }

  onShowLessArtifacts() {
    this.numberOfArtifacts.set(DEFAULT_NUMBER_OF_LIST_ITEMS);
  }

  onSearchSubmit($e: Event, form: HTMLFormElement) {
    $e.preventDefault();
    $e.stopPropagation();
    const formData = new FormData(form);
    this.searchSubmit.emit({
      query: formData.get('query') as string
    });
  }

  onEditStoredSearchesClick() {
    this.editStoredSearchesClick.emit();
  }

  onSearchSuggestionClick($event: MouseEvent, suggestion: SearchSuggestion) {
    $event.preventDefault();
    $event.stopPropagation();
    this.searchSuggestionClick.emit(suggestion);
  }

  onRecentRegistrationClick(registration: RecentRegistration) {
    this.recentRegistrationClick.emit(registration);
  }

  onOnlyMyOwnRecentRegistrationsChange({ value }: { value: boolean }) {
    this.onlyMyOwnRegistrationsChange.emit(value);
  }
}
